import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const containerVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.05 } },
};

const itemVariants = {
  hidden: { opacity: 0, x: -16 },
  visible: { opacity: 1, x: 0, transition: { type: 'spring', stiffness: 300, damping: 26 } },
  exit: { opacity: 0, x: 16, transition: { duration: 0.15 } },
};

function Produtos({ video, produtos, setVisivel, setProdutoSelecionado, setProdutos }) {
  const [deletando, setDeletando] = useState(null);
  const [deletandoTodos, setDeletandoTodos] = useState(false);
  const [mostrarTodos, setMostrarTodos] = useState(false);
  const [visiveisCount, setVisiveisCount] = useState(4);
  const panelRef = useRef(null);

  // Calcula quantos cards cabem dinamicamente
  useEffect(() => {
    const calcularVisiveis = () => {
      if (!panelRef.current || mostrarTodos) return;
      const panelH = panelRef.current.getBoundingClientRect().height;
      const headerH = 140; // aprox header + add button
      const cardH = 72;    // aprox altura de cada card
      const navH = 64;     // altura dos botoes Voltar/Continuar
      const espaco = panelH - headerH - navH - 32; // padding
      const count = Math.max(3, Math.floor(espaco / cardH));
      setVisiveisCount(count);
    };
    calcularVisiveis();
    const ro = new ResizeObserver(calcularVisiveis);
    if (panelRef.current) ro.observe(panelRef.current);
    return () => ro.disconnect();
  }, [produtos.length, mostrarTodos]);

  function editarProduto(produto) {
    setProdutoSelecionado(produto);
    setVisivel('editar-produto');
  }

  async function deletarProduto(e, id) {
    e.stopPropagation();
    if (!window.confirm('Deseja realmente deletar este produto?')) return;

    setDeletando(id);
    try {
      const res = await fetch('https://videooferta.com/api/deletar-produto.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id }),
      });
      const data = await res.json();
      if (data.success) {
        setProdutos((prev) => prev.filter((p) => p.id !== id));
      } else {
        alert(data.msg);
      }
    } finally {
      setDeletando(null);
    }
  }

  async function deletarTodosProdutos() {
    if (!window.confirm('Deseja realmente remover TODOS os produtos?')) return;

    setDeletandoTodos(true);
    try {
      const res = await fetch('https://videooferta.com/api/deletar-todos-produtos.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id_video: video.video_id }),
      });
      const data = await res.json();
      if (data.success) {
        setProdutos([]);
      } else {
        alert(data.msg || 'Erro ao remover produtos');
      }
    } finally {
      setDeletandoTodos(false);
    }
  }

  const LIMITE = 10;

  const temMais = produtos.length > visiveisCount && !mostrarTodos;
  const produtosFiltrados = mostrarTodos ? produtos : produtos.slice(0, visiveisCount);

  return (
    <div ref={panelRef} className="col2 prod-panel" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* --- Header --- */}
      <motion.div
        className="prod-header"
        initial={{ opacity: 0, y: -12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35 }}
      >
        <div className="prod-header__top">
          <div className="prod-header__title-group">
            <div className="prod-header__icon-wrap">
              <i className="fa-solid fa-cart-shopping" />
            </div>
            <div>
              <h2 className="prod-header__title">Produtos</h2>
              <p className="prod-header__subtitle">
                {produtos.length} / {LIMITE} produtos
              </p>
            </div>
          </div>
          <button
            className="prod-header__close"
            onClick={() => setVisivel('')}
            aria-label="Fechar"
          >
            <i className="fa-solid fa-xmark" />
          </button>
        </div>

        <button
          className="prod-add-btn"
          onClick={() => setVisivel('add-produto')}
          disabled={produtos.length >= LIMITE}
        >
          <i className="fa-solid fa-plus" />
          Adicionar produtos
        </button>
      </motion.div>

      {/* --- Body --- */}
      <div className="prod-body" style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        {produtos.length === 0 ? (
          <motion.div
            className="prod-empty"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.15 }}
          >
            <div className="prod-empty__icon">
              <i className="fa-solid fa-box-open" />
            </div>
            <p className="prod-empty__title">Nenhum produto adicionado</p>
            <p className="prod-empty__desc">
              Adicione produtos para montar seu video de ofertas.
            </p>
            <button
              className="prod-empty__btn"
              onClick={() => setVisivel('add-produto')}
            >
              <i className="fa-solid fa-plus" /> Adicionar primeiro produto
            </button>
          </motion.div>
        ) : (
          <>
            {/* --- Lista --- */}
            <div className="prod-list-header">
              <span className="prod-list-header__label">
                Produtos adicionados
              </span>
              {produtos.length > 1 && (
                <button
                  className="prod-list-header__clear"
                  onClick={deletarTodosProdutos}
                  disabled={deletandoTodos}
                >
                  {deletandoTodos ? (
                    <span className="prod-spinner-sm" />
                  ) : (
                    <>
                      <i className="fa-solid fa-trash-can" /> Remover todos
                    </>
                  )}
                </button>
              )}
            </div>

            <motion.div
              className="prod-list"
              variants={containerVariants}
              initial="hidden"
              animate="visible"
              style={{ flex: 1, overflowY: mostrarTodos ? 'auto' : 'hidden' }}
            >
              <AnimatePresence mode="popLayout">
                {produtosFiltrados.map((prod) => (
                  <motion.div
                    key={prod.id}
                    className="prod-card"
                    variants={itemVariants}
                    layout
                    whileHover={{ y: -2, transition: { duration: 0.15 } }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => editarProduto(prod)}
                  >
                    <div className="prod-card__img-wrap">
                      <img
                        src={prod.imagem}
                        alt={prod.titulo}
                        className="prod-card__img"
                      />
                    </div>
                    <div className="prod-card__info">
                      <p className="prod-card__name">{prod.titulo}</p>
                      {prod.preco && prod.preco !== '0,00' && (
                        <span className="prod-card__price">R$ {prod.preco}</span>
                      )}
                    </div>
                    <button
                      className="prod-card__delete"
                      onClick={(e) => deletarProduto(e, prod.id)}
                      disabled={deletando === prod.id}
                      aria-label="Deletar"
                    >
                      {deletando === prod.id ? (
                        <span className="prod-spinner-sm" />
                      ) : (
                        <i className="fa-solid fa-trash" />
                      )}
                    </button>
                  </motion.div>
                ))}
              </AnimatePresence>
            </motion.div>

            {temMais && (
              <button className="prod-list-ver-todos" onClick={() => setMostrarTodos(true)}>
                <i className="fa-solid fa-chevron-down" /> Ver lista completa
              </button>
            )}

            <div className="prod-list-nav" style={{ display: 'flex', gap: 10, paddingTop: 10, borderTop: '1px solid #E8E1EF', alignItems: 'center' }}>
              <button className="prod-back-btn" onClick={() => setVisivel('temas')} style={{ flex: 1 }}>
                <i className="fa-solid fa-arrow-left" /> Voltar
              </button>
              <button className="prod-add-btn" onClick={() => setVisivel('locucao')} style={{ flex: 1 }}>
                Continuar <i className="fa-solid fa-arrow-right" />
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default Produtos;
